import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiCall } from '../../../../services/realApi';
import { useAuth } from '../../../../contexts/AuthContext';
import { useClient } from '../../../../contexts/ClientContext';
import { Modal, Input, Select, Button } from '../../../../components/ui';
import { MODULES, PERMISSIONS, moduleDescriptions } from '../../../../utils/permissions';
import { usePermissions } from '../../../../hooks/usePermissions';
import { FiAlertCircle as AlertCircle, FiCheckCircle as CheckCircle, FiInfo as Info, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp } from 'react-icons/fi';

const JOB_TITLES = [
  // Level 1
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'solution_architect', label: 'Solution Architect' },
  { value: 'technical_account_manager', label: 'Technical Account Manager' },
  // Level 2
  { value: 'district_manager', label: 'District Manager' },
  // Level 3
  { value: 'partner_sales_manager', label: 'Partner Sales Manager' }
];

interface SelectableUser {
  user_id: string;
  name?: string | null;
  email?: string | null;
  job_title?: string | null;
}

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  allUsers?: SelectableUser[];
  inviteContext?: Record<string, string> | null;
  currentUserId?: string;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  allUsers = [],
  inviteContext = null,
  currentUserId
}) => {
  const { user: currentUser } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const { selectedClientId } = useClient();
  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    job_title: string;
    manager_id: string;
    client_id: string;
    partners: string[];
    clients: string[];
    modules: string[];
    permissions: string[];
  }>({
    name: '',
    email: '',
    job_title: '',
    manager_id: '',
    client_id: '',
    partners: [],
    clients: [],
    modules: [],
    permissions: []
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{ manager_name: string; manager_email: string; client_count: number; module_count?: number; role_count?: number } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showModulesSection, setShowModulesSection] = useState(false);
  const [showPermissionsSection, setShowPermissionsSection] = useState(false);
  const [expandedPermissionModules, setExpandedPermissionModules] = useState<Record<string, boolean>>({});
  const [clients, setClients] = useState<{ id: string; name: string; email?: string }[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [allPermissions, setAllPermissions] = useState<{ name: string; description?: string }[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const isSystemAdmin = isAdmin || hasPermission(PERMISSIONS.SYSTEM_ADMIN);
  const isClientUser = Boolean(currentUser?.client_id);
  const hasPartnerScope = Boolean(currentUser?.partner_id);
  
  // Get current user's available modules and permissions
  const allModules = useMemo(() => Object.values(MODULES), []);
  const availableModules = useMemo(() => {
    if (isSystemAdmin) return allModules;
    return currentUser?.aws_accessible_modules || [];
  }, [currentUser, isSystemAdmin, allModules]);
  const availablePermissions = useMemo(
    () => {
      if (isSystemAdmin && allPermissions.length > 0) return allPermissions;
      return currentUser?.permissions || [];
    },
    [currentUser, isSystemAdmin, allPermissions]
  );
  
  // Module labels mapping
  const moduleLabels = {
    [MODULES.HUB]: 'Hub',
    [MODULES.PLATFORMOPS]: 'CloudOps',
    [MODULES.DATAOPS]: 'DataOps',
    [MODULES.GENAIOPS]: 'GenAIOps',
    [MODULES.AGENTOPS]: 'AgentOps'
  };

  const permissionModuleOrder = [
    MODULES.HUB,
    MODULES.PLATFORMOPS,
    MODULES.DATAOPS,
    MODULES.GENAIOPS,
    MODULES.AGENTOPS
  ];

  const getPermissionModule = (permission: string | { name: string; description?: string }) => {
    const name = typeof permission === 'string' ? permission : permission?.name || '';
    const resource = name.split('.')[0];

    switch (resource) {
      case 'platformops':
        return MODULES.PLATFORMOPS;
      case 'dataops':
        return MODULES.DATAOPS;
      case 'genaiops':
        return MODULES.GENAIOPS;
      case 'agentops':
        return MODULES.AGENTOPS;
      default:
        return MODULES.HUB;
    }
  };

  const groupedPermissions = useMemo(() => {
    return availablePermissions.reduce<Record<string, { name: string; description?: string }[]>>((acc, permission) => {
      const moduleKey = getPermissionModule(permission);
      if (!acc[moduleKey]) {
        acc[moduleKey] = [];
      }
      // Normalize: strings become objects so downstream code can always access .name
      const normalized = typeof permission === 'string' ? { name: permission } : permission;
      acc[moduleKey].push(normalized);
      return acc;
    }, {});
  }, [availablePermissions]);

  const loadAllPermissions = async () => {
    if (!isSystemAdmin) return;
    setPermissionsLoading(true);
    try {
      const data = await apiCall('/roles/permissions');
      setAllPermissions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setAllPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadPreview = useCallback(async (managerId: string) => {
    try {
      const manager = allUsers.find(u => u.user_id === managerId);
      if (manager) {
        // Fetch manager's details to show what will be inherited
        const managerDetails = await apiCall(`/aws-users/${managerId}`);
        setPreviewData({
          manager_name: manager.name ?? '',
          manager_email: manager.email ?? '',
          client_count: managerDetails?.client_count || 0,
          module_count: managerDetails?.module_count || 0,
          role_count: managerDetails?.role_count || 0
        });
        setShowPreview(true);
      }
    } catch (err) {
      console.error('Error loading preview:', err);
    }
  }, [allUsers]);

  useEffect(() => {
    if (inviteContext?.manager_id) {
      setFormData(prev => ({
        ...prev,
        manager_id: inviteContext.manager_id
      }));
      // Load preview when manager is set
      if (inviteContext.manager_id) {
        loadPreview(inviteContext.manager_id);
      }
    }
  }, [inviteContext, loadPreview]);

  // Clients don't have manager context, so hide and clear it
  useEffect(() => {
    if (isOpen && isClientUser) {
      setFormData(prev => ({
        ...prev,
        manager_id: ''
      }));
      setPreviewData(null);
      setShowPreview(false);
    }
  }, [isOpen, isClientUser]);
  
  // Load clients when modal opens for aivilo_staff users
  useEffect(() => {
    if (isOpen && isSystemAdmin) {
      loadClients();
      loadAllPermissions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSystemAdmin]);

  // Initialize with all modules and permissions selected by default
  useEffect(() => {
    if (isOpen && currentUser) {
      const userModules = availableModules || [];
      const userPermissions = availablePermissions || [];
      const defaultModules = [...userModules];
      const defaultPermissions = userPermissions.map(p => p.name);
      
      setFormData(prev => ({
        ...prev,
        modules: defaultModules,
        permissions: defaultPermissions
      }));
    }
  }, [isOpen, currentUser, availableModules, availablePermissions]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const data = await apiCall('/clients');
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };


  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

    // Load preview when manager changes
    if (field === 'manager_id' && value) {
      loadPreview(value);
    } else if (field === 'manager_id' && !value) {
      setShowPreview(false);
      setPreviewData(null);
    }
  };
  
  const handleModuleToggle = (module: string) => {
    setFormData(prev => {
      const currentModules = prev.modules || [];
      const isSelected = currentModules.includes(module);
      return {
        ...prev,
        modules: isSelected
          ? currentModules.filter(m => m !== module)
          : [...currentModules, module]
      };
    });
  };
  
  const handlePermissionToggle = (permission: string) => {
    setFormData(prev => {
      const currentPermissions = prev.permissions || [];
      const isSelected = currentPermissions.includes(permission);
      return {
        ...prev,
        permissions: isSelected
          ? currentPermissions.filter(p => p !== permission)
          : [...currentPermissions, permission]
      };
    });
  };
  
  const handleSelectAllModules = () => {
    setFormData(prev => ({
      ...prev,
      modules: [...availableModules]
    }));
  };
  
  const handleDeselectAllModules = () => {
    setFormData(prev => ({
      ...prev,
      modules: []
    }));
  };
  
  const handleSelectAllPermissions = () => {
    const allPermissionNames = availablePermissions.map(p => p.name);
    setFormData(prev => ({
      ...prev,
      permissions: allPermissionNames
    }));
  };
  
  const handleDeselectAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: []
    }));
  };

  const togglePermissionModule = (moduleKey: string) => {
    setExpandedPermissionModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleModulePermissionsToggle = (moduleKey: string) => {
    const modulePermissions = groupedPermissions[moduleKey] || [];
    const modulePermissionNames = modulePermissions.map(p => p.name);
    const allSelected = modulePermissionNames.every(name => formData.permissions?.includes(name));

    setFormData(prev => {
      const currentPermissions = prev.permissions || [];
      if (allSelected) {
        return {
          ...prev,
          permissions: currentPermissions.filter(p => !modulePermissionNames.includes(p))
        };
      } else {
        const newPermissions = Array.from(new Set([...currentPermissions, ...modulePermissionNames]));
        return {
          ...prev,
          permissions: newPermissions
        };
      }
    });
  };

  const validateEmail = (email: string): string | null => {
    if (!email) return 'Email is required';
    // Amazon email validation skipped for now
    // if (!email.endsWith('@amazon.com')) {
    //   return 'AWS users must have an @amazon.com email address';
    // }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return null;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    const emailError = validateEmail(formData.email);
    if (emailError) {
      newErrors.email = emailError;
    }

    if (!formData.job_title) {
      newErrors.job_title = 'Job title is required';
    }

    // Validate client_id for aivilo_staff users
    if (isSystemAdmin && !formData.client_id) {
      newErrors.client_id = 'Client is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!currentUserId) {
      setErrors({ submit: 'Current user ID is missing. Please refresh the page.' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payload: Record<string, unknown> = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        job_title: formData.job_title,
        invited_by: currentUserId
      };

      if (formData.manager_id) {
        payload.manager_id = formData.manager_id;
      }

      if ((formData.partners?.length ?? 0) > 0) {
        payload.partners = formData.partners;
      }

      // For partner users, always use the selected client ID
      if (hasPartnerScope && selectedClientId) {
        payload.clients = [selectedClientId];
      } else if (isSystemAdmin && formData.client_id) {
        // For aivilo_staff users, use client_id
        payload.client_id = formData.client_id;
      } else if ((formData.clients?.length ?? 0) > 0) {
        // For other users, use formData clients if available
        payload.clients = formData.clients;
      }

      // Add selected modules to inherit
      if ((formData.modules?.length ?? 0) > 0) {
        payload.modules = formData.modules;
      }

      // Add selected permissions to inherit
      if ((formData.permissions?.length ?? 0) > 0) {
        payload.permissions = formData.permissions;
      }

      await apiCall('/aws-users', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        job_title: '',
        manager_id: '',
        client_id: '',
        partners: [],
        clients: [],
        modules: [],
        permissions: []
      });
      setErrors({});
      setPreviewData(null);
      setShowPreview(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      console.error('Error inviting user:', err);
      setErrors({
        submit: (err instanceof Error ? err.message : null) || 'Failed to invite user. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      job_title: '',
      manager_id: '',
      client_id: '',
      partners: [],
      clients: [],
      modules: [],
      permissions: []
    });
    setErrors({});
    setPreviewData(null);
    setShowPreview(false);
    setShowModulesSection(false);
    setShowPermissionsSection(false);
    setExpandedPermissionModules({});
    onClose();
  };

  const managerOptions = allUsers.map(user => ({
    value: user.user_id,
    label: `${user.name} (${user.email})` + (user.job_title ? ` - ${user.job_title.replaceAll('_', ' ')}` : '')
  }));

  const clientOptions = clients.map(client => ({
    value: client.id,
    label: `${client.name}` + (client.email ? ` (${client.email})` : '')
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite AWS User"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Permission Inheritance</p>
            <p>
              Select which modules and permissions you want the invited user to inherit. 
              By default, all your available modules and permissions are selected.
            </p>
          </div>
        </div>

        {/* Name */}
        <Input
          label="Name"
          value={formData.name}
          onChange={handleChange('name')}
          placeholder="Enter full name"
          required
          error={errors.name}
        />

        {/* Email */}
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          placeholder="user@amazon.com"
          required
          error={errors.email}
        />

        {/* Job Title */}
        <Select
          label="Job Title"
          value={formData.job_title}
          onChange={handleChange('job_title')}
          options={JOB_TITLES}
          required
          error={errors.job_title}
          placeholder="Select job title"
        />

        {/* Manager (Optional) - hide for clients */}
        {!isClientUser && (
          <Select
            label="Manager (Optional)"
            value={formData.manager_id}
            onChange={handleChange('manager_id')}
            options={managerOptions}
            placeholder="Select manager (optional)"
            helpText="Select a manager to establish hierarchy relationship"
          />
        )}

        {/* Client Selector - Only for aivilo_staff */}
        {isSystemAdmin && (
          <Select
            label="Client"
            value={formData.client_id}
            onChange={handleChange('client_id')}
            options={clientOptions}
            placeholder={loadingClients ? "Loading clients..." : "Select a client"}
            required
            error={errors.client_id}
            helpText="Select the client this AWS user will be associated with"
          />
        )}

        {/* Modules Selection */}
        {availableModules.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <button
              type="button"
              onClick={() => setShowModulesSection(!showModulesSection)}
              className="w-full flex items-center justify-between text-left"
            >
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Modules to Inherit
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.modules?.length || 0} of {availableModules.length} selected
                </p>
              </div>
              {showModulesSection ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {showModulesSection && (
              <div className="mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Select modules to grant access
                  </span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={handleSelectAllModules}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllModules}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableModules.map((module) => (
                    <label
                      key={module}
                      className="flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.modules?.includes(module) || false}
                        onChange={() => handleModuleToggle(module)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {(moduleLabels as Record<string, string>)[module] || module}
                        </span>
                        {(moduleDescriptions as Record<string, string>)[module] && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {(moduleDescriptions as Record<string, string>)[module]}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Permissions Selection */}
        {(permissionsLoading || availablePermissions.length > 0) && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <button
              type="button"
              onClick={() => setShowPermissionsSection(!showPermissionsSection)}
              className="w-full flex items-center justify-between text-left"
            >
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Permissions to Inherit
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.permissions?.length || 0} of {availablePermissions.length} selected
                </p>
              </div>
              {showPermissionsSection ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {showPermissionsSection && (
              <div className="mt-4 space-y-3">
                {permissionsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Select permissions to grant
                      </span>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={handleSelectAllPermissions}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleDeselectAllPermissions}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {permissionModuleOrder.map((moduleKey) => {
                        const modulePermissions = groupedPermissions[moduleKey] || [];
                        if (modulePermissions.length === 0) {
                          return null;
                        }

                        const selectedCount = modulePermissions.filter(permission =>
                          formData.permissions?.includes(permission.name)
                        ).length;

                        const allModuleSelected = modulePermissions.every(p => formData.permissions?.includes(p.name));
                        const someModuleSelected = !allModuleSelected && modulePermissions.some(p => formData.permissions?.includes(p.name));

                        return (
                          <div key={moduleKey} className="border border-gray-200 dark:border-gray-700 rounded-md">
                            <div className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md">
                              <input
                                type="checkbox"
                                checked={allModuleSelected}
                                ref={el => {
                                  if (el) el.indeterminate = someModuleSelected;
                                }}
                                onChange={() => handleModulePermissionsToggle(moduleKey)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mr-3 flex-shrink-0"
                              />
                              <button
                                type="button"
                                onClick={() => togglePermissionModule(moduleKey)}
                                className="flex-1 flex items-center justify-between text-left"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {moduleLabels[moduleKey] || moduleKey}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                      {selectedCount} of {modulePermissions.length}
                                    </span>
                                  </div>
                                  {moduleDescriptions[moduleKey] && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {moduleDescriptions[moduleKey]}
                                    </p>
                                  )}
                                </div>
                                {expandedPermissionModules[moduleKey] ? (
                                  <ChevronUp className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </div>

                            {expandedPermissionModules[moduleKey] && (
                              <div className="px-2 pb-2 space-y-1">
                                {modulePermissions.map((permission) => (
                                  <label aria-label={permission.description || permission.name}
                                    key={permission.name}
                                    className="flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions?.includes(permission.name) || false}
                                      onChange={() => handlePermissionToggle(permission.name)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm text-gray-900 dark:text-white">
                                        {permission.description || permission.name}
                                      </span>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {permission.name}
                                      </p>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preview of what will be inherited */}
        {showPreview && previewData && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800 dark:text-green-300">
                <p className="font-medium mb-2">
                  This user will inherit permissions from {previewData.manager_name}:
                </p>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <p className="font-semibold">{previewData.client_count}</p>
                    <p className="text-xs">Clients</p>
                  </div>
                  <div>
                    <p className="font-semibold">{formData.modules?.length || 0}</p>
                    <p className="text-xs">Modules</p>
                  </div>
                  <div>
                    <p className="font-semibold">{formData.permissions?.length || 0}</p>
                    <p className="text-xs">Permissions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            Invite User
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default InviteUserModal;

